/**
 * Shared OAuth Store
 *
 * A configurable OAuth store that can be used by both UI and SI applications.
 * Handles token management, login, logout, and session restoration.
 *
 * SECURITY ARCHITECTURE:
 * - Access tokens: Memory only (XSS protection)
 * - Refresh tokens: HttpOnly secure cookies (web) or secure storage (native)
 * - CSRF protection: Double-submit cookie pattern
 *
 * FEATURES:
 * - Cross-tab synchronization via BroadcastChannel API
 * - Distributed refresh lock to prevent duplicate token refreshes
 * - Maintenance mode detection (503 responses)
 */

import { defineStore } from 'pinia';
import axios from 'axios';
import type { AxiosInstance } from 'axios';
import {
  type OAuthTokenData,
  type OAuthState,
  type OAuthConfig,
  type OAuthErrorType,
  type SecureAuthResponse,
  type LoginApiResponse,
  type TwoFactorChallengeResponse,
  type AuthSyncMessage,
  type CrossTabSyncConfig,
  type MaintenanceModeState,
  type MaintenanceModeCallback,
  type LogoutCallback,
  type SecureStorageAdapter,
  convertSecureAuthResponse,
  detectOAuthErrorType,
  getCsrfToken,
  hasSessionMarkerCookie,
} from './types';
import { authEndpoints } from './endpoints';
import { devError, devWarn } from '@shared/vue/utils/debug';

/**
 * OAuth store configuration
 */
export interface OAuthStoreConfig extends OAuthConfig {
  /** Store ID (unique per app) */
  storeId?: string;
  /** App identifier for session conflict detection ('ui' or 'si') */
  appId: 'ui' | 'si';
  /** Callback when logout is triggered */
  onLogout?: LogoutCallback;
  /**
   * Callback with the raw auth payload after a successful login — lets the
   * app consume login-delivered config (e.g. the `remote_logging` block)
   * without re-fetching /me. Invoked best-effort; errors are swallowed.
   */
  onAuthData?: (data: SecureAuthResponse['data']) => void;
  /** Whether this is a native app (iOS/Android) */
  isNativeApp?: boolean;
  /**
   * Secure storage adapter for native apps (iOS/Android)
   * Used to persist refresh tokens in encrypted storage instead of cookies
   * Required when isNativeApp is true
   */
  secureStorageAdapter?: SecureStorageAdapter;
  /** Cross-tab synchronization configuration */
  crossTabSync?: CrossTabSyncConfig;
  /** Callback when maintenance mode state changes */
  onMaintenanceMode?: MaintenanceModeCallback;
}

/**
 * Default store ID
 */
const DEFAULT_STORE_ID = 'oauth';

/**
 * Refresh waiter timeout (10 seconds)
 */
const REFRESH_WAITER_TIMEOUT = 10000;

/**
 * Module-level state for cross-tab coordination
 * Stored outside Pinia to share across store instances and prevent serialization issues
 */
interface CrossTabState {
  authChannel: BroadcastChannel | null;
  isRefreshing: boolean;
  refreshWaiters: Array<(token: OAuthTokenData | null) => void>;
}

const crossTabStateMap = new Map<string, CrossTabState>();

function getCrossTabState(channelName: string): CrossTabState {
  if (!crossTabStateMap.has(channelName)) {
    crossTabStateMap.set(channelName, {
      authChannel: null,
      isRefreshing: false,
      refreshWaiters: [],
    });
  }
  return crossTabStateMap.get(channelName)!;
}

/**
 * Create a configured OAuth store
 *
 * @param config - OAuth configuration
 * @returns Pinia store definition
 *
 * @example
 * ```ts
 * // In your app's store file
 * import { createOAuthStore } from '@shared/auth';
 *
 * export const useOAuthStore = createOAuthStore({
 *   apiUrl: import.meta.env.VITE_API_URL,
 *   clientId: import.meta.env.VITE_OAUTH_CLIENT_ID,
 *   clientSecret: import.meta.env.VITE_OAUTH_CLIENT_SECRET,
 *   storeId: 'store-oauth',
 *   crossTabSync: {
 *     channelName: 'my-app-auth',
 *     enabled: true,
 *   },
 *   onLogout: () => {
 *     // Clear app-specific state
 *   },
 *   onMaintenanceMode: (isActive, message, retryAfter) => {
 *     // Handle maintenance mode
 *   },
 * });
 * ```
 */
export function createOAuthStore(config: OAuthStoreConfig) {
  const {
    apiUrl,
    clientId,
    timeout = 10000,
    storeId = DEFAULT_STORE_ID,
    appId,
    onLogout,
    onAuthData,
    isNativeApp = false,
    secureStorageAdapter,
    crossTabSync,
    onMaintenanceMode,
  } = config;

  // Determine if cross-tab sync should be enabled
  const crossTabEnabled =
    crossTabSync?.enabled !== false && !isNativeApp && typeof BroadcastChannel !== 'undefined';
  const channelName = crossTabSync?.channelName || `${storeId}-auth`;

  // Lazy OAuth API instance.
  //
  // Why lazy? In Capacitor's bundled production build (Vite 8 / rolldown-vite)
  // the entry chunk imports `useOAuthStore` from this module *and* this module
  // imports `axios` from the entry chunk — a real ESM cycle. If `axios.create`
  // ran here at factory-call time, it would execute before the entry chunk had
  // finished initializing axios, and the binding would resolve to `undefined`,
  // throwing `TypeError: undefined is not an object (evaluating 'axios.create')`
  // at app boot and leaving the WKWebView blank. Deferring the call until an
  // action actually needs the instance ensures axios is fully initialized by
  // the time we read it.
  let _oauthApi: AxiosInstance | null = null;
  const oauthApi = (): AxiosInstance =>
    (_oauthApi ??= axios.create({
      baseURL: apiUrl,
      timeout,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }));

  return defineStore(storeId, {
    state: (): OAuthState & {
      twoFactorChallenge: null | {
        challengeToken: string;
        method: 'totp' | 'email' | 'sms';
        expiresIn: number;
        expiresAt: string;
      };
    } => ({
      tokenData: null,
      lastError: null,
      deliberateLogout: false,
      isRestoringSession: false,
      justLoggedIn: false,
      isNewUser: false,
      maintenanceMode: {
        isActive: false,
        message: null,
        retryAfter: null,
      },
      twoFactorChallenge: null,
    }),

    getters: {
      /** Check if user is logged in */
      isLoggedIn: (state): boolean => !!state.tokenData?.accessToken,

      /** Check if token exists */
      hasToken: (state): boolean => !!state.tokenData?.accessToken,

      /** Get access token */
      accessToken: (state): string | null => state.tokenData?.accessToken || null,

      /** The full token record (access + refresh + expiry). */
      getToken: (state): OAuthTokenData | null => state.tokenData,

      /** Whether this tab just completed a login. */
      hasJustLoggedIn: (state): boolean => state.justLoggedIn,

      /** True when the last successful login was a new user's first login (email/password flow) */
      lastIsNewUser: (state): boolean => state.isNewUser,

      /** Check if token is expired (with 30s buffer) */
      isTokenExpired: (state): boolean => {
        if (!state.tokenData?.expiresAt) return true;
        const bufferMs = 30 * 1000;
        return new Date(state.tokenData.expiresAt).getTime() - bufferMs <= Date.now();
      },

      /** Get authorization header */
      getAuthHeader: (state): string | null => {
        if (!state.tokenData?.accessToken) return null;
        return `${state.tokenData.tokenType} ${state.tokenData.accessToken}`;
      },

      /** Get last error */
      getLastError: (state) => state.lastError,

      /** Check if maintenance mode is active */
      isMaintenanceMode: (state): boolean => state.maintenanceMode.isActive,

      /** Get maintenance mode message */
      maintenanceMessage: (state): string | null => state.maintenanceMode.message,

      /** Get maintenance mode retry-after value */
      maintenanceRetryAfter: (state): number | null => state.maintenanceMode.retryAfter,
    },

    actions: {
      // ==========================================
      // Cross-Tab Synchronization
      // ==========================================

      /**
       * Initialize cross-tab synchronization
       * Call this in a boot file or app initialization
       */
      initCrossTabSync(): void {
        if (!crossTabEnabled) return;

        const state = getCrossTabState(channelName);

        // Already initialized
        if (state.authChannel) return;

        try {
          state.authChannel = new BroadcastChannel(channelName);
          state.authChannel.onmessage = (event: MessageEvent<AuthSyncMessage>) => {
            this._handleCrossTabMessage(event.data);
          };
        } catch (error) {
          devWarn('[OAuth] Failed to initialize BroadcastChannel:', error);
        }
      },

      /**
       * Cleanup cross-tab synchronization
       * Call this before app unmounts or on beforeunload
       */
      cleanupCrossTabSync(): void {
        if (!crossTabEnabled) return;

        const state = getCrossTabState(channelName);
        if (state.authChannel) {
          state.authChannel.close();
          state.authChannel = null;
        }
      },

      /**
       * Handle incoming cross-tab message
       * @internal
       */
      _handleCrossTabMessage(message: AuthSyncMessage): void {
        const state = getCrossTabState(channelName);

        switch (message.type) {
          case 'refresh-start':
            // Another tab started refreshing - mark as refreshing
            state.isRefreshing = true;
            break;

          case 'refresh-success':
            // Another tab successfully refreshed - update our token
            if (message.tokenData) {
              this.setToken(message.tokenData);
            }
            state.isRefreshing = false;
            // Notify any waiters in this tab
            state.refreshWaiters.forEach((resolve) => resolve(message.tokenData || null));
            state.refreshWaiters = [];
            break;

          case 'refresh-failed':
            // Another tab's refresh failed
            state.isRefreshing = false;
            // Notify waiters with null (failure)
            state.refreshWaiters.forEach((resolve) => resolve(null));
            state.refreshWaiters = [];
            break;

          case 'logout':
            // Another tab logged out - clear our state and redirect
            this.clearToken(false); // Don't broadcast back
            // Run the app's logout cleanup in this tab too. The comment
            // above always promised this ("App should handle redirect in
            // onLogout callback") but the call was never wired, so
            // receiving tabs kept the previous user's scoped store state.
            if (onLogout) {
              void Promise.resolve(onLogout()).catch((error) => {
                devWarn('[OAuth] Cross-tab onLogout cleanup failed:', error);
              });
            }
            break;
        }
      },

      /**
       * Broadcast message to other tabs
       * @internal
       */
      _broadcastMessage(message: AuthSyncMessage): void {
        if (!crossTabEnabled) return;

        const state = getCrossTabState(channelName);
        if (state.authChannel) {
          try {
            state.authChannel.postMessage(message);
          } catch (error) {
            devWarn('[OAuth] Failed to broadcast message:', error);
          }
        }
      },

      // ==========================================
      // Maintenance Mode
      // ==========================================

      /**
       * Set maintenance mode state
       */
      setMaintenanceMode(isActive: boolean, message?: string, retryAfter?: number): void {
        this.maintenanceMode = {
          isActive,
          message: message || null,
          retryAfter: retryAfter || null,
        };

        // Invoke callback if provided
        if (onMaintenanceMode) {
          onMaintenanceMode(isActive, message, retryAfter);
        }
      },

      /**
       * Clear maintenance mode
       */
      clearMaintenanceMode(): void {
        this.setMaintenanceMode(false);
      },

      // ==========================================
      // Just Logged In Flag
      // ==========================================

      /**
       * Set just-logged-in flag
       * Used by apps for post-login flows (e.g., redirect, welcome message)
       */
      setJustLoggedIn(value: boolean): void {
        this.justLoggedIn = value;
      },

      /**
       * Clear just-logged-in flag
       */
      clearJustLoggedIn(): void {
        this.justLoggedIn = false;
      },

      // ==========================================
      // Core Authentication
      // ==========================================

      /**
       * Fetch CSRF token from backend
       */
      async fetchCsrfToken(): Promise<string | null> {
        try {
          const response = await oauthApi().get<{ data: { csrf_token: string } }>(
            authEndpoints.csrfToken(appId),
            {
              headers: {
                'X-EMALINO-App-Context': appId, // Send app context for multi-app cookie isolation
              },
              withCredentials: true, // Required to receive CSRF cookie from cross-origin response
            }
          );
          return response.data?.data?.csrf_token || null;
        } catch (error) {
          // Check for maintenance mode
          this._checkMaintenanceMode(error);
          return null;
        }
      },

      /**
       * Login with email and password
       * @param email - User email
       * @param password - User password
       * @param recaptchaToken - Optional reCAPTCHA Enterprise token captured
       *   by `useRecaptcha().getToken('login')`. Forwarded to the API as
       *   `recaptcha_token` (snake_case to match the V1 contract). The API
       *   makes the field required iff `GOOGLE_RECAPTCHA_ENABLED=true` on
       *   the backend — native apps skip recaptcha via `useRecaptcha`'s
       *   `isNativePlatform` short-circuit, so `undefined` is safe there.
       */
      async login(
        email: string,
        password: string,
        recaptchaToken?: string,
        consent?: { cookie: boolean; terms: boolean; privacy: boolean; accepted_at: string },
      ): Promise<boolean> {
        this.lastError = null;
        this.clearMaintenanceMode();

        try {
          // Build headers - include native app indicator for Android/iOS
          const headers: Record<string, string> = {
            'X-EMALINO-App-Context': appId, // Send app context for tracking
          };

          // On native apps, request refresh_token in response body (instead of HttpOnly cookie)
          if (isNativeApp) {
            headers['X-EMALINO-Native-App'] = 'true';
          }

          // Call central /auth/login endpoint (browser-safe OAuth flow with HttpOnly cookies)
          // The endpoint detects app context from client_id and sets app-specific cookies automatically
          const response = await oauthApi().post<LoginApiResponse>(
            authEndpoints.login(),
            {
              email,
              password,
              client_id: clientId,
              // client_secret intentionally omitted — the API injects the
              // confidential secret server-side (by client_id/app context), so
              // it is never shipped in the JS bundle. See V1AuthController::multiAppLogin.
              // recaptcha gate — API verifies via `Recaptcha::verifyRequest`
              // when reCAPTCHA is enabled. Conditional spread keeps the
              // field absent (not null) when no token is captured, which
              // matches the `nullable|string` validation rule when
              // reCAPTCHA is disabled and the `required|string` rule when
              // reCAPTCHA is enabled.
              ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
              // GDPR consent gate — the API rejects with 412 + error_type:
              // consent_required if this is missing/false on UI requests.
              // SI bypasses the gate via X-EMALINO-App-Context: si, so it's
              // safe to send `undefined` from SI callers.
              ...(consent ? { consent } : {}),
            },
            {
              headers,
              withCredentials: true, // Required to receive refresh token cookie from response
            }
          );

          const responseData = response.data.data;

          if (responseData && 'requires_2fa' in responseData && responseData.requires_2fa === true) {
            const challenge = responseData as TwoFactorChallengeResponse;
            this.lastError = {
              type: '2fa_required',
              message: '2FA verification required',
            };
            this.twoFactorChallenge = {
              challengeToken: challenge.challenge_token,
              method: challenge.method,
              expiresIn: challenge.expires_in,
              expiresAt: new Date(Date.now() + challenge.expires_in * 1000).toISOString(),
            };
            return false;
          }

          const authData = responseData as SecureAuthResponse['data'] | undefined;

          if (!authData?.access_token) {
            throw new Error('Login failed: No access token received');
          }

          // Store access token in memory
          const tokenData: OAuthTokenData = {
            accessToken: authData.access_token,
            tokenType: authData.token_type,
            expiresIn: authData.expires_in,
            expiresAt: new Date(Date.now() + authData.expires_in * 1000).toISOString(),
          };
          this.setToken(tokenData);

          // On native apps: Store refresh token in secure storage (persists across app restarts)
          // On web: Refresh token is stored in HttpOnly cookie by backend
          if (isNativeApp && secureStorageAdapter && authData.refresh_token) {
            await secureStorageAdapter.setRefreshToken(
              authData.refresh_token,
              authData.refresh_token_expires_at
            );
          }

          // Fetch CSRF token for future requests
          await this.fetchCsrfToken();

          // On native apps: Also store CSRF token in secure storage
          if (isNativeApp && secureStorageAdapter) {
            const csrfToken = getCsrfToken(appId);
            if (csrfToken) {
              await secureStorageAdapter.setCsrfToken(csrfToken);
            }
          }

          // Set just logged in flag for post-login flows
          this.setJustLoggedIn(true);

          // Propagate is_new_user from the backend — set on first login after registration.
          this.isNewUser = authData.is_new_user === true;

          // Hand the raw payload to the app (login-delivered config such as
          // remote_logging). Best-effort: a consumer error never fails login.
          if (onAuthData) {
            try {
              onAuthData(authData);
            } catch (callbackError) {
              devWarn('[OAuth] onAuthData callback failed:', callbackError);
            }
          }

          return true;
        } catch (error: unknown) {
          this._checkMaintenanceMode(error);
          this.handleAuthError(error);
          return false;
        }
      },

      /**
       * Refresh access token with distributed lock
       * Prevents multiple tabs from refreshing simultaneously
       *
       * On native apps (Android/iOS): Uses refresh token from secure storage
       * On web: Uses refresh token from HttpOnly cookie
       *
       * Cross-tab serialisation is via the Web Locks API when available
       * (modern browsers). BroadcastChannel alone has a TOCTTOU race:
       * two tabs can both pass the state.isRefreshing gate before either's
       * `refresh-start` message has been delivered, both call
       * /auth/{ui|si}/refresh, the loser 401s on the rotated refresh token,
       * and the loser's clearCookies() wipes the winner's fresh cookie —
       * leaving both tabs to redirect to /login on the next 401. The Web
       * Lock named `${channelName}-refresh` makes refresh calls strictly
       * sequential per origin, so the second tab either reuses the broadcast
       * token or refreshes with the already-rotated cookie. Native and any
       * browser without locks falls back to the BroadcastChannel-only path.
       */
      async refreshAccessToken(): Promise<OAuthTokenData | null> {
        const canUseWebLock =
          !isNativeApp &&
          typeof navigator !== 'undefined' &&
          typeof navigator.locks?.request === 'function';

        if (canUseWebLock) {
          return navigator.locks.request(
            `${channelName}-refresh`,
            { mode: 'exclusive' },
            async () => {
              // After acquiring the lock, the winning tab may have already
              // pushed fresh tokenData into our store via the cross-tab
              // `refresh-success` handler. Reuse it instead of presenting
              // an already-rotated refresh token a second time.
              if (this.tokenData?.accessToken && !this.isTokenExpired) {
                return this.tokenData;
              }
              // Holding the Web Lock means we are the canonical refresher
              // right now. Reset state.isRefreshing so _performTokenRefresh
              // doesn't queue us on a stale broadcast that will never
              // resolve (can happen if a previous holder crashed after
              // broadcasting `refresh-start` but before `refresh-success`).
              // Any orphaned refreshWaiters will be notified by our refresh
              // itself when it broadcasts success/failure.
              getCrossTabState(channelName).isRefreshing = false;
              return this._performTokenRefresh();
            }
          );
        }

        return this._performTokenRefresh();
      },

      /**
       * Actual refresh call. Wrapped by refreshAccessToken so cross-tab
       * serialisation can be applied externally via the Web Locks API.
       * @internal
       */
      async _performTokenRefresh(): Promise<OAuthTokenData | null> {
        const state = getCrossTabState(channelName);

        // If another caller in this tab is already refreshing, wait for the
        // result. With Web Locks, this branch handles same-tab concurrent
        // callers when no lock is in play (e.g. native, or the rare browser
        // without Locks support).
        if (crossTabEnabled && state.isRefreshing) {
          return new Promise<OAuthTokenData | null>((resolve) => {
            // Set up timeout to prevent indefinite waiting
            const timeoutId = setTimeout(() => {
              const index = state.refreshWaiters.indexOf(resolve);
              if (index > -1) {
                state.refreshWaiters.splice(index, 1);
              }
              resolve(null); // Timeout - return null
            }, REFRESH_WAITER_TIMEOUT);

            // Add to waiters with cleanup on resolve
            const resolveWithCleanup = (token: OAuthTokenData | null) => {
              clearTimeout(timeoutId);
              resolve(token);
            };
            state.refreshWaiters.push(resolveWithCleanup);
          });
        }

        // Acquire the refresh lock
        state.isRefreshing = true;

        // Broadcast that we're starting a refresh (web only)
        this._broadcastMessage({
          type: 'refresh-start',
          timestamp: Date.now(),
        });

        try {
          // Get CSRF token - from cookie (web) or secure storage (native)
          let csrfToken: string | null = null;
          if (isNativeApp && secureStorageAdapter) {
            csrfToken = await secureStorageAdapter.getCsrfToken();
          }
          if (!csrfToken) {
            csrfToken = getCsrfToken(appId);
          }
          if (!csrfToken) {
            throw new Error('No CSRF token available');
          }

          // Build headers
          const headers: Record<string, string> = {
            'X-EMALINO-CSRF-Token': csrfToken,
            'X-EMALINO-App-Context': appId, // Send app context for multi-app cookie isolation
          };

          // Build request body
          // On native: include refresh_token from secure storage
          // On web: empty body (refresh token sent via HttpOnly cookie)
          let requestBody: Record<string, string> = {};

          if (isNativeApp && secureStorageAdapter) {
            headers['X-EMALINO-Native-App'] = 'true';
            const refreshToken = await secureStorageAdapter.getRefreshToken();
            if (!refreshToken) {
              throw new Error('No refresh token in secure storage');
            }
            requestBody = { refresh_token: refreshToken };
          }

          const response = await oauthApi().post<SecureAuthResponse>(
            authEndpoints.refresh(appId),
            requestBody,
            {
              headers,
              withCredentials: true, // Required to send refresh token cookie and receive new tokens
            }
          );

          if (!response.data.data?.access_token) {
            throw new Error('No access token received');
          }

          const tokenData = convertSecureAuthResponse(response.data.data);
          this.setToken(tokenData);

          // On native: update refresh token in secure storage if a new one was returned
          if (isNativeApp && secureStorageAdapter && response.data.data.refresh_token) {
            await secureStorageAdapter.setRefreshToken(
              response.data.data.refresh_token,
              response.data.data.refresh_token_expires_at
            );
          }

          // On native: update CSRF token in secure storage
          if (isNativeApp && secureStorageAdapter) {
            const newCsrfToken = getCsrfToken(appId);
            if (newCsrfToken) {
              await secureStorageAdapter.setCsrfToken(newCsrfToken);
            }
          }

          // Broadcast success to other tabs (web only)
          this._broadcastMessage({
            type: 'refresh-success',
            timestamp: Date.now(),
            tokenData,
          });

          // Notify local waiters
          state.refreshWaiters.forEach((resolve) => resolve(tokenData));
          state.refreshWaiters = [];

          return tokenData;
        } catch (error: unknown) {
          this._checkMaintenanceMode(error);
          this.handleAuthError(error);
          this.clearToken(false); // Don't broadcast logout on refresh failure

          // On native: clear secure storage on refresh failure
          if (isNativeApp && secureStorageAdapter) {
            await secureStorageAdapter.clearAll();
          }

          // On web: clear cookies on refresh failure (prevents stale cookie persistence)
          // This is critical when the backend is migrated fresh - old cookies must be cleared
          if (!isNativeApp) {
            try {
              await oauthApi().post(authEndpoints.clearCookies(appId), {}, {
                headers: {
                  'X-EMALINO-App-Context': appId,
                },
                withCredentials: true,
              });
            } catch (cookieError) {
              // Log but don't throw - refresh already failed
              devError('[refreshAccessToken] Failed to clear cookies:', cookieError);
            }
          }

          // Broadcast failure to other tabs (web only)
          this._broadcastMessage({
            type: 'refresh-failed',
            timestamp: Date.now(),
          });

          // Notify local waiters of failure
          state.refreshWaiters.forEach((resolve) => resolve(null));
          state.refreshWaiters = [];

          return null;
        } finally {
          // Release the refresh lock
          state.isRefreshing = false;
        }
      },

      /**
       * Restore session from HttpOnly cookie (web) or SecureStorage (native)
       *
       * Web: gated on a non-HttpOnly session-marker cookie set by the API
       * alongside the HttpOnly refresh token. If the marker is absent, no
       * session exists in this browser — return early without hitting the
       * network. This is what keeps the console quiet for anonymous visitors
       * (login page, marketing pages) and after a fresh DB migration where
       * stale cookies have already been cleared.
       */
      async restoreSession(): Promise<boolean> {
        if (this.isRestoringSession) {
          return false;
        }

        // Web fast-path: skip the blind refresh attempt when the marker
        // cookie says there's no session to restore.
        if (!isNativeApp && !hasSessionMarkerCookie(appId)) {
          return false;
        }

        this.isRestoringSession = true;

        try {
          // Fetch CSRF token first
          const csrfToken = await this.fetchCsrfToken();
          if (!csrfToken) {
            return false;
          }

          // On native apps: Store the newly fetched CSRF token in SecureStorage
          // This is critical! The server just generated a new CSRF token, and we need
          // to use this same token in the refresh request. Otherwise the tokens won't match.
          if (isNativeApp && secureStorageAdapter) {
            await secureStorageAdapter.setCsrfToken(csrfToken);
          }

          // Try to refresh token
          const tokenData = await this.refreshAccessToken();

          if (!tokenData) {
            devWarn('[restoreSession] Token refresh failed - likely invalid/expired cookie or fresh DB migration');
            devWarn('[restoreSession] Cookies have been cleared to prevent stale auth state');
          }

          return !!tokenData;
        } catch (error) {
          devError('[restoreSession] Error:', error);
          this.clearToken();
          return false;
        } finally {
          this.isRestoringSession = false;
        }
      },

      /**
       * Logout user
       * @param broadcastLogout - Whether to broadcast logout to other tabs (default: true)
       */
      async logout(broadcastLogout = true): Promise<void> {
        this.deliberateLogout = true;

        try {
          // Revoke tokens on server
          if (this.tokenData?.accessToken) {
            await oauthApi().post(
              authEndpoints.logout(),
              {},
              {
                headers: {
                  Authorization: `${this.tokenData.tokenType} ${this.tokenData.accessToken}`,
                  'X-EMALINO-App-Context': appId,
                },
                withCredentials: true,
              }
            );
          }
        } catch {
          // Continue with logout even if token revocation fails
        }

        // Clear cookies (web) or skip for native (cookies not used)
        if (!isNativeApp) {
          try {
            await oauthApi().post(authEndpoints.clearCookies(appId), {}, {
              headers: {
                'X-EMALINO-App-Context': appId,
              },
              withCredentials: true,
            });
          } catch {
            // Continue with logout even if cookie clearing fails
          }
        }

        // On native: clear secure storage
        if (isNativeApp && secureStorageAdapter) {
          try {
            await secureStorageAdapter.clearAll();
          } catch {
            // Continue with logout even if secure storage clearing fails
          }
        }

        // Clear local state
        this.clearToken(broadcastLogout);

        // Call app-specific logout handler
        if (onLogout) {
          await onLogout();
        }

        // Reset flag after delay
        setTimeout(() => {
          this.deliberateLogout = false;
        }, 1000);
      },

      /**
       * Set token data
       */
      setToken(tokenData: OAuthTokenData): void {
        this.tokenData = tokenData;
        this.lastError = null;
      },

      /**
       * Clear token data
       * @param broadcastLogout - Whether to broadcast logout to other tabs (default: true)
       */
      clearToken(broadcastLogout = true): void {
        this.tokenData = null;
        this.justLoggedIn = false;
        this.isNewUser = false;

        // Broadcast logout to other tabs
        if (broadcastLogout) {
          this._broadcastMessage({
            type: 'logout',
            timestamp: Date.now(),
          });
        }
      },

      /**
       * Set error
       */
      setError(type: OAuthErrorType, message: string): void {
        this.lastError = { type, message };
      },

      /**
       * Clear error
       */
      clearError(): void {
        this.lastError = null;
      },

      /**
       * Clear two-factor challenge state
       */
      clearTwoFactorChallenge(): void {
        this.twoFactorChallenge = null;
      },

      /**
       * Handle authentication errors
       */
      handleAuthError(error: unknown): void {
        if (axios.isAxiosError(error) && error.response?.data) {
          const data = error.response.data as Record<string, unknown>;

          // Try OAuth2 standard format first (error, error_description)
          const oauthError = detectOAuthErrorType(data);
          if (oauthError) {
            this.lastError = {
              type: oauthError.errorType,
              message: oauthError.message,
            };
            return;
          }

          // Try Laravel API error format (status: "error", message: "...")
          if (data.status === 'error' && typeof data.message === 'string') {
            const errorType = typeof data.error_type === 'string' ? data.error_type : 'unknown';
            // Collapsing everything except authentication_error to 'unknown'
            // destroyed the taxonomy downstream: LoginPage branches on
            // email_not_verified to land the user on the resend screen, and
            // that branch could never fire — the type died right here.
            // Surfaced by the email-signup e2e.
            const mapped: OAuthErrorType =
              errorType === 'authentication_error'
                ? 'invalid_credentials'
                : errorType === 'email_not_verified'
                  ? 'email_not_verified'
                  : 'unknown';
            this.lastError = {
              type: mapped,
              message: data.message,
            };
            return;
          }
        }

        this.lastError = {
          type: 'unknown',
          message: error instanceof Error ? error.message : 'Unknown authentication error',
        };
      },

      /**
       * Check if error is a maintenance mode response (503)
       * @internal
       */
      _checkMaintenanceMode(error: unknown): void {
        if (axios.isAxiosError(error) && error.response?.status === 503) {
          const message = error.response.data?.message || 'System maintenance in progress';
          const retryAfterHeader = error.response.headers?.['retry-after'];
          const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;

          this.setMaintenanceMode(true, message, retryAfter || undefined);
        }
      },
    },

    // No persistence - tokens are memory-only for security
  });
}

/**
 * Re-export types for convenience
 */
export type {
  OAuthTokenData,
  OAuthState,
  OAuthConfig,
  OAuthErrorType,
  AuthSyncMessage,
  CrossTabSyncConfig,
  MaintenanceModeState,
  MaintenanceModeCallback,
  LogoutCallback,
};
